import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";
import moment from "moment";
import { useMutation, useQuery } from "@apollo/client";

import { FormGroup, Card, Table, PageHeader, Button } from "@cofano/cck";
import { Box, Grid2 as Grid } from "@mui/material";

import Helmet from "components/helmet/Helmet";

import { Language, DataFormat, getPluginSettingValue, hasPluginPermission, usePermission } from "imports/Utils";

import { FormattedDate } from "utils/DateUtils";

import LabelTag from "view/labels/components/tags/LabelTag";
import StatusChip from "view/statuses/components/changer/StatusChip";
import SailingReportStatusChanger from "view/sailingreports/components/status/SailingReportStatusChanger";

import { SailingReportRangeType } from "./utils/SailingReportUtils";

import { ChildProps } from "types/commons";

import { GetSailingReportDocument } from "__generated__/view/sailingreports/graphql/sailingReport.queries.generated";
import { GetEmissionReportDocument, GetEmissionReportMutation } from "__generated__/view/sailingreports/graphql/sailingReport.mutations.generated";

type EmissionReportFromQuery = ChildProps<GetEmissionReportMutation, "emissionReport">;

const SailingReportDetails = () => {

	const session = useStoreState(state => state.session);
	const navigate = useNavigate();
	const { id } = useParams();

	const { isReadOnly, isRTR, isFinance, isAdmin } = usePermission();

	const [ emissionReport, setEmissionReport ] = useState<EmissionReportFromQuery | null>(null);

	const { data, refetch } = useQuery(GetSailingReportDocument, {
		variables: {
			id: Number(id),
		},
	});

	const [ fetchEmissionReport, { loading: isLoading } ] = useMutation(GetEmissionReportDocument);

	const rangeType = getPluginSettingValue(session, "sailingReportRangeType");
	const sailingReport = data?.sailingReport;
	const type = sailingReport?.fromTime && sailingReport?.toTime ? rangeType === SailingReportRangeType.Week ? 3 : 2 : 1;

	const fetchAndUpdateEmissionReport = () => {
		fetchEmissionReport({
			variables: {
				sailingReportId: Number(id),
			},
		}).then((response) => {
			setEmissionReport(response?.data?.emissionReport ?? null);
		});
	};

	return (
		<div>
			<PageHeader>
				<PageHeader.Title>
					{Language("SAILING_REPORT")}
					<Helmet title={intl.formatMessage({ id: "SAILING_REPORT" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					<Button onClick={() => navigate("/view/sailingreports")} variant="outlined">
						{Language("SAILING_REPORTS")}
					</Button>

					{!isReadOnly && (
						<Button onClick={() => navigate(`/view/sailingreports/${id}/edit`)}>
							{Language("EDIT")}
						</Button>
					)}
				</PageHeader.Actions>
			</PageHeader>

			<Grid spacing={3} container>
				<Grid size={4}>
					<Card
						title={Language("DETAILS")}
						actions={[ {
							hidden: !hasPluginPermission(session, "sailingReportApprovals") || !sailingReport?.status,
							render: () =>
								(isFinance || isAdmin) ? (
									<SailingReportStatusChanger
										sailingReportId={Number(id)}
										currentStatus={sailingReport!.status}
										statusChanges={sailingReport!.statusChanges}
										refetch={refetch}
									/>
								) : (
									<StatusChip
										label={intl.formatMessage({ id: `${sailingReport!.status.languageKeyPrefix}${sailingReport!.status.name}` })}
										color={sailingReport!.status.color}
									/>
								),
						} ]}
					>
						<FormGroup label={Language("SHIP")} labelPlacement="left-center" overflow="hidden">
							{sailingReport?.ship?.name || "-"}
						</FormGroup>

						{type === 1 && (
							<FormGroup label={Language("VOYAGE")} labelPlacement="left-center" overflow="hidden">
								{sailingReport?.voyage?.voyageNumber || "-"}
							</FormGroup>
						)}

						{type === 2 && (
							<React.Fragment>
								<FormGroup label={Language("FROM")} labelPlacement="left-center" overflow="hidden">
									<FormattedDate date={sailingReport?.fromTime} format="ll" />
								</FormGroup>

								<FormGroup label={Language("TO")} labelPlacement="left-center" overflow="hidden">
									<FormattedDate date={sailingReport?.toTime} format="ll" />
								</FormGroup>
							</React.Fragment>
						)}

						{type === 3 && (
							<React.Fragment>
								<FormGroup label={Language("CONTRACT_WEEK")} labelPlacement="left-center" overflow="hidden">
									{`${moment(sailingReport?.fromTime).isoWeek()} - ${moment(sailingReport?.fromTime).year()}`}
								</FormGroup>
							</React.Fragment>
						)}

						<FormGroup label={Language("SAILINGREPORT_REMARK")} labelPlacement="left-center" overflow="hidden">
							{sailingReport?.remark || "-"}
						</FormGroup>

						<FormGroup label={Language("SAILINGREPORT_SAILMINUTES")} labelPlacement="left-center" overflow="hidden">
							{DataFormat.minutesToHours(sailingReport?.sailMinutes) || "-"}
						</FormGroup>

						{isRTR && (
							<FormGroup
								label={Language("SAILINGREPORT_GEOFENCE_SAILMINUTES")}
								labelPlacement="left-center">
								{DataFormat.minutesToHours(sailingReport?.geofenceSailMinutes) || "-"}
							</FormGroup>
						)}

						{isRTR && (
							<FormGroup
								label={Language("SAILINGREPORT_SAILED_DISTANCE")}
								labelPlacement="left-center">
								{DataFormat.distance(sailingReport?.sailedDistanceKm) ?? "-"}
							</FormGroup>
						)}

						<FormGroup label={Language("BUNKERING_BEFORE")} labelPlacement="left-center" overflow="hidden">
							{DataFormat.volumeL(sailingReport?.bunkeringBefore) || "-"}
						</FormGroup>

						<FormGroup label={Language("BUNKERED")} labelPlacement="left-center" overflow="hidden">
							{DataFormat.volumeL(sailingReport?.bunkered) || "-"}
						</FormGroup>

						<FormGroup label={Language("BUNKERING_AFTER")} labelPlacement="left-center" overflow="hidden">
							{DataFormat.volumeL(sailingReport?.bunkeringAfter) || "-"}
						</FormGroup>

						<FormGroup label={Language("SHIPS_FUEL_TYPE")} labelPlacement="left-center" overflow="hidden">
							{sailingReport?.fuelType ? intl.formatMessage({ id: `SHIP_FUEL_${sailingReport?.fuelType}` }) : "-"}
						</FormGroup>

						<FormGroup label={Language("USAGE_AMOUNT")} labelPlacement="left-center" overflow="hidden">
							{DataFormat.volumeL(sailingReport?.usageAmount) || "-"}
						</FormGroup>

						<FormGroup label={Language("USAGE_PER_HOUR")} labelPlacement="left-center" overflow="hidden">
							{DataFormat.volumeL(sailingReport?.usagePerHour) || "-"}
						</FormGroup>

						<FormGroup label={Language("EMISSION_CO2")} labelPlacement="left-center" overflow="hidden">
							{DataFormat.volumeKg(sailingReport?.emissionCO2) || "-"}
						</FormGroup>

						<FormGroup label={Language("LABEL")} labelPlacement="left-center" overflow="hidden">
							{sailingReport?.label ? <LabelTag label={sailingReport.label} /> : "-"}
						</FormGroup>
					</Card>

					{sailingReport?.ship?.advancedTracking && (
						<Box mt={2}>
							<Card title={Language("SHIP_ACTUALS")}>
								<FormGroup label={Language("USAGE_AMOUNT")} labelPlacement="left-center" overflow="hidden">
									{DataFormat.volumeL(emissionReport?.consumption?.total ?? sailingReport.actualUsageAmount) || "-"}
								</FormGroup>

								<FormGroup label={Language("EMISSION_CO2")} labelPlacement="left-center" overflow="hidden">
									{DataFormat.volumeKg(emissionReport?.co2?.total ?? sailingReport.actualEmissionCO2) || "-"}
								</FormGroup>

								<FormGroup label={Language("DISTANCE_KM")} labelPlacement="left-center" overflow="hidden">
									{DataFormat.distance(emissionReport?.distance?.distanceInKm ?? sailingReport.actualSailedDistanceKm) || "-"}
								</FormGroup>

								<Box mt={2}>
									<Button
										onClick={fetchAndUpdateEmissionReport}
										loading={isLoading}
										size="small">
										{Language("RECALCULATE")}
									</Button>
								</Box>
							</Card>
						</Box>
					)}
				</Grid>

				<Grid size={8}>
					<Card title={Language("SALINGREPORT_LOGS")} disablePadding>
						<Table
							id="sailingReportLogs"
							disableFooter
							content={sailingReport?.logs || []}
							columns={[ {
								id: "allDay",
								title: intl.formatMessage({ id: "ALL_DAY" }),
								render: row => DataFormat.boolean(row.allDay),
							}, {
								id: "startDate",
								title: intl.formatMessage({ id: "SAILINGREPORT_STARTDATE" }),
								render: (row) => <FormattedDate date={row.startDate} format={row.allDay ? "ll" : undefined} withoutYear />,
							}, {
								id: "endDate",
								title: intl.formatMessage({ id: "SAILINGREPORT_ENDDATE" }),
								render: (row) => <FormattedDate date={row.endDate} format={row.allDay ? "ll" : undefined} withoutYear />,
							}, {
								id: "remark",
								title: intl.formatMessage({ id: "SAILINGREPORT_REMARK" }),
							}, {
								id: "startLocation",
								title: intl.formatMessage({ id: "SAILINGREPORT_STARTLOCATION" }),
							}, {
								id: "endLocation",
								title: intl.formatMessage({ id: "SAILINGREPORT_ENDLOCATION" }),
							} ]} />
					</Card>
				</Grid>
			</Grid>
		</div>
	);
};

export default SailingReportDetails;

